import { Box, Tooltip, Typography } from '@mui/material';
import { Base } from 'components/Containers/BaseContainer';
import { ThemePalette } from 'mui.theme';
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'store/hook';
import { generateRandomColor, pastelColorMap } from 'utils/helpers/general';
import { isDarkTheme } from 'utils/theme';
import ComponentsLineChart from '../../Parts/Components/LineChart';
import { ComponentsPieChart } from '../../Parts/Components/PieChart';
import { extractSelectedAssets } from '../../Parts/utils';
import useComponentCostBreakdown from '../hooks/useComponentCostBreakdown';
import OverflowingTitles from './OverflowingTitles';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface ComponentsCostBreakdownProps {
  selectedCell: string[];
}

const ComponentsCostBreakdown = ({
  selectedCell,
}: ComponentsCostBreakdownProps) => {
  const { t } = useTranslation();
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const assetComponentsCostsData = useAppSelector(
    (state) => state.squaresDashboardReducer?.dashboard?.assetComponentsCosts
  );
  const [selectedComponentsData, setSelectedComponentsData] = useState<{
    components: string[];
    index: number[];
  }>({
    components: [],
    index: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  const handlePieClick = (data: any, index: number) => {
    const name = data?.name;
    if (name) {
      setSelectedComponentsData(() => ({
        components: [name],
        index: [index],
      }));
    }
  };

  const { pieChartData } = useComponentCostBreakdown({
    selectedAssets: selectedCell,
  });

  useEffect(() => {
    if (!pieChartData || pieChartData.length === 0) return;
    // since data is sorted highest will always be at top
    const topElement = pieChartData[0];
    setSelectedComponentsData({
      components: [topElement?.name],
      index: [0],
    });
    setIsLoading(false);
  }, [pieChartData]);

  const lineChartData = useMemo(() => {
    if (assetComponentsCostsData) {
      const temp = extractSelectedAssets(
        assetComponentsCostsData,
        selectedCell,
        selectedComponentsData?.components?.[0]
      );
      return temp;
    }
  }, [
    assetComponentsCostsData,
    selectedCell,
    selectedComponentsData.components,
  ]);

  const top5CostingComponents = [...pieChartData].splice(0, 5);

  const textColor = palette.textPrimary;

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
        width={'100%'}
      >
        <Typography
          sx={{
            fontSize: '20px',
            color: palette.textPrimary,
          }}
        >
          {t('cdp_loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Base
      enableDarkTheme={enableDarkTheme}
      sx={{
        width: '100%',
        margin: '0px',
        maxWidth: '100%',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)', // 5 equal-width columns
          gap: '8px',
          padding: '20px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ gridColumn: 'span 2', width: '100%' }}>
          <Base
            sx={{
              height: '500px',
              maxHeight: '500px',
              margin: '0px',
              backgroundColor: enableDarkTheme
                ? palette.toolBarBackground
                : palette.toolBarBackground,
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '8px',
                  paddingTop: '30px',
                }}
              >
                <Typography
                  sx={{
                    textAlign: 'center',
                    fontSize: '15px',
                    color: textColor,
                  }}
                >
                  {`Component wise cost breakdown for `}
                </Typography>
                <Typography
                  sx={{
                    textAlign: 'center',
                    fontSize: '14px',
                    color: textColor,
                  }}
                >
                  {`${selectedCell?.slice(0, 2)?.join(', ')}`}
                </Typography>
              </div>
              <OverflowingTitles allTitles={selectedCell} overflowAfter={2} />
            </Box>

            <div>
              <div>
                <div style={{ padding: '12px' }}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: textColor,
                      textAlign: 'center',
                    }}
                  >
                    {t('top_5_costing_components')}
                  </Typography>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '4px',
                      alignItems: 'center',
                    }}
                  >
                    {top5CostingComponents.map((item, index) => {
                      return (
                        <Box
                          key={index}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              backgroundColor:
                                pastelColorMap[index] || generateRandomColor(),
                              borderRadius: 1,
                            }}
                          />
                          <Tooltip title={item?.name} arrow>
                            <Typography
                              sx={{
                                fontSize: '11px',
                                color: palette.textPrimary,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100px',
                                width: '100%',
                              }}
                            >
                              {item.name}
                            </Typography>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </div>
                </div>
                <ComponentsPieChart
                  data={pieChartData}
                  handlePieClick={handlePieClick}
                  enableDarkTheme={enableDarkTheme}
                  onlyOneActive
                  label={t('component')}
                  valueLabel="Total Component Cost"
                  activeIndices={selectedComponentsData.index}
                />
              </div>
            </div>
          </Base>
        </div>
        <div style={{ gridColumn: 'span 3', width: '100%' }}>
          <Base
            sx={{
              height: '400px',
              maxHeight: '400px',
              margin: '0px',
              backgroundColor: enableDarkTheme
                ? palette.toolBarBackground
                : palette.toolBarBackground,
            }}
          >
            {selectedComponentsData.components?.length < 1 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{
                    textAlign: 'center',
                    fontSize: '20px',
                    paddingTop: '10px',
                    color: palette.textPrimary,
                  }}
                >
                  {t('please_select_a_component_from_the_pie_chart')}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: 'center',
                      fontSize: '14px',
                      paddingTop: '30px',
                      color: textColor,
                    }}
                  >
                    {`Distribution of ${
                      selectedComponentsData?.components?.[0]
                    } for ${selectedCell?.slice(0, 2)?.join(', ')}`}
                  </Typography>
                  <OverflowingTitles
                    allTitles={selectedCell}
                    overflowAfter={2}
                  />
                </Box>
                <Box sx={{ width: '100%', height: '320px' }}>
                  <ComponentsLineChart
                    data={lineChartData}
                    height="100%"
                    darkBgColor={ThemePalette?.dark?.toolBarBackground}
                  />
                </Box>
              </Box>
            )}
          </Base>
        </div>
      </div>
    </Base>
  );
};

export default ComponentsCostBreakdown;
